import React, { useState, useEffect, useRef } from 'react';
import './Configurator.css';
import GarageSelector, { config3D } from './GarageSelector';

// Assets
import logo from './g6sample.png'; // short panel
import lpanel from './lpanelram.PNG'; // long panel
import bsamp from './BeardBoardSamp.PNG'; // beadboard
import fpanel from './flush.png'; // flush

import window4paneWhite from './windows/4pane_white.png';
import windowPrairieWhite from './windows/prairie_white.png';
import windowDiamondWhite from './windows/diamond_white.png';
import windowSunriseWhite from './windows/sunrise1_white.png';
import window12paneWhite from './windows/12pane_white.png';
import flushwindowWhite from './windows/flushwindow_white.png';

const colorsList = [
  { id: 'white', label: 'White', hex: '#E8E8E8' },
  { id: 'almond', label: 'Almond', hex: '#DED0BC' },
  { id: 'wickertan', label: 'Wicker Tan', hex: '#C0B7A1' },
  { id: 'sandtone', label: 'Sandtone', hex: '#B7A592' },
  { id: 'brown', label: 'Dark Brown', hex: '#3A2D23' },
  { id: 'black', label: 'Black', hex: '#1B1B1B' },
  { id: 'terratone', label: 'Terratone', hex: '#635B51' },
  { id: 'charcoalgray', label: 'Charcoal Gray', hex: '#565A5C' },
];

const Configurator = () => {
  const [size, setSize] = useState('16ft');
  const [panel, setPanel] = useState('long');
  const [color, setColor] = useState('white');
  const [windows, setWindows] = useState('none');
  
  // Fine tuning state
  const [fineTuneMode, setFineTuneMode] = useState(false);
  const [customConfigs, setCustomConfigs] = useState(null);

  useEffect(() => {
    if (!customConfigs && Object.keys(config3D).length > 0) {
      setCustomConfigs(JSON.parse(JSON.stringify(config3D)));
    }
  }, [customConfigs]);

  const handleConfigChange = (sizerVal, key, value) => {
    setCustomConfigs(prev => ({
      ...prev,
      [sizerVal]: {
        ...prev[sizerVal],
        [key]: value
      }
    }));
  };
  
  // For scaling the GarageSelector properly within the left pane
  const previewWrapperRef = useRef(null);
  const [scaleFactor, setScaleFactor] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      if (previewWrapperRef.current) {
        const parentWidth = previewWrapperRef.current.clientWidth;
        const parentHeight = previewWrapperRef.current.clientHeight;
        
        // The GarageSelector has a base width of 800px and a max height of roughly 800px.
        // Calculate the maximum scale that fits within both dimensions (with a 5% margin)
        const scaleX = parentWidth / 800;
        const scaleY = parentHeight / 800;
        const targetScale = Math.min(scaleX, scaleY) * 0.95;
        
        setScaleFactor(targetScale);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePanelChange = (newPanel) => {
    setPanel(newPanel);
    let newWindow = windows;
    if (newPanel === 'long' && newWindow !== 'none' && newWindow !== '12pane') {
      newWindow = 'none';
    } else if (newPanel === 'flush' && newWindow !== 'none' && newWindow !== 'flushLeft' && newWindow !== 'flushRight') {
      newWindow = 'none';
    } else if (newPanel !== 'long' && newPanel !== 'flush' && (newWindow === '12pane' || newWindow === 'flushLeft' || newWindow === 'flushRight')) {
      newWindow = 'none';
    }
    setWindows(newWindow);
  };

  // Sunrise now spans 2 panels, so it works on 14ft doors

  let sizeValue = 16;
  if (size === '14ft') sizeValue = 14;
  else if (size === '8ft') sizeValue = 8;

  return (
    <div className="Configurator-App">
      {/* LEFT PANE: LIVE PREVIEW */}
      <div className="preview-pane">
        <div className="preview-wrapper" ref={previewWrapperRef}>
          <div style={{ transform: `scale(${scaleFactor})`, transformOrigin: 'center center' }}>
            <GarageSelector 
              sizer={sizeValue} 
              colors={color} 
              panelss={panel} 
              windows={windows} 
              customConfig={customConfigs ? customConfigs[sizeValue] : undefined}
            />
          </div>
        </div>
        
        {/* SELECTION OVERLAY */}
        <div className="selection-overlay" style={{ right: `calc(50% - ${400 * scaleFactor}px - 30px)` }}>
          <h4>Your Design</h4>
          <p><strong>Size:</strong> {size}</p>
          <p><strong>Panel:</strong> {panel === 'beadBoard' ? 'BeadBoard' : panel.charAt(0).toUpperCase() + panel.slice(1)}</p>
          <p><strong>Color:</strong> {color.charAt(0).toUpperCase() + color.slice(1)}</p>
          <p><strong>Windows:</strong> {
            windows === 'none' ? 'No Windows' :
            windows === '4pane' ? 'Stockton (4-Pane)' :
            windows === 'prairie' ? 'Prairie' :
            windows === 'diamond' ? 'Waterford' :
            windows === 'sunrise' ? 'Wagon Wheel' :
            windows === '12pane' ? 'Stockton (12-Pane)' :
            windows === 'flushLeft' ? 'Left Windows' :
            windows === 'flushRight' ? 'Right Windows' : windows
          }</p>
        </div>
        {/* Hidden fine tune button 
        <button 
          style={{ position: 'absolute', bottom: '10px', left: '10px', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', zIndex: 100 }}
          onClick={() => setFineTuneMode(!fineTuneMode)}
        >
          {fineTuneMode ? 'Close Fine Tune' : 'Fine Tune Position'}
        </button>
        */}
      </div>

      {/* RIGHT PANE: CONTROLS */}
      <div className="controls-pane">
        <div className="controls-header">
          <span className="controls-label">CUSTOMIZER</span>
          <h2 className="controls-heading">Design Your <br/><em style={{ color: 'var(--gold, #C9A84C)', fontStyle: 'italic' }}>Garage Door</em></h2>
          <div className="controls-rule"></div>
          <p className="controls-text">Customize every detail to match your home.</p>
        </div>

        {fineTuneMode && customConfigs && customConfigs[sizeValue] && (
          <div className="control-section" style={{ background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '8px' }}>
            <h3 className="control-section-title" style={{ color: '#fff' }}>Fine Tune {sizeValue}ft</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px', fontSize: '12px' }}>
              {['top', 'left', 'scale', 'stretchX', 'stretchY'].map(key => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column' }}>
                  <label>{key}: {customConfigs[sizeValue][key]}</label>
                  <input 
                    type="range" 
                    min={key === 'scale' || key.includes('stretch') ? "0.1" : "-200"} 
                    max={key === 'scale' || key.includes('stretch') ? "2.0" : "1000"} 
                    step={key === 'scale' || key.includes('stretch') ? "0.01" : "1"} 
                    value={customConfigs[sizeValue][key]} 
                    onChange={(e) => handleConfigChange(sizeValue, key, parseFloat(e.target.value))} 
                  />
                </div>
              ))}
              {['rotateY', 'rotateZ', 'skewY'].map(key => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column' }}>
                  <label>{key}: {customConfigs[sizeValue][key]}</label>
                  <input 
                    type="text" 
                    value={customConfigs[sizeValue][key]} 
                    onChange={(e) => handleConfigChange(sizeValue, key, e.target.value)} 
                    style={{ background: '#222', color: '#fff', border: '1px solid #444', padding: '2px 5px' }}
                  />
                </div>
              ))}
            </div>
            <button onClick={() => console.log(JSON.stringify(customConfigs, null, 2))} style={{ marginTop: '10px', background: 'var(--gold)', color: '#000', border: 'none', padding: '5px' }}>Print to Console</button>
          </div>
        )}

        {/* DOOR SIZE */}
        <div className="control-section">
          <h3 className="control-section-title">1. Door Size</h3>
          <div className="size-selector">
            <div className={`size-option ${size === '16ft' ? 'selected' : ''}`} onClick={() => setSize('16ft')}>16' x 7'</div>
            <div className={`size-option ${size === '14ft' ? 'selected' : ''}`} onClick={() => setSize('14ft')}>14' x 7'</div>
            <div className={`size-option ${size === '8ft' ? 'selected' : ''}`} onClick={() => setSize('8ft')}>8' x 7'</div>
          </div>
        </div>

        {/* PANEL DESIGN */}
        <div className="control-section">
          <h3 className="control-section-title">2. Panel Design</h3>
          <div className="visual-selector">
            <div className={`visual-option-card ${panel === 'long' ? 'selected' : ''}`} onClick={() => handlePanelChange('long')}>
              <img src={lpanel} alt="Long Panel" />
              <span className="visual-option-label">Long</span>
            </div>
            <div className={`visual-option-card ${panel === 'short' ? 'selected' : ''}`} onClick={() => handlePanelChange('short')}>
              <img src={logo} alt="Short Panel" />
              <span className="visual-option-label">Short</span>
            </div>
            <div className={`visual-option-card ${panel === 'beadBoard' ? 'selected' : ''}`} onClick={() => handlePanelChange('beadBoard')}>
              <img src={bsamp} alt="BeadBoard" />
              <span className="visual-option-label">BeadBoard</span>
            </div>
            <div className={`visual-option-card ${panel === 'flush' ? 'selected' : ''}`} onClick={() => handlePanelChange('flush')}>
              <img src={fpanel} alt="Flush" />
              <span className="visual-option-label">Flush</span>
            </div>
          </div>
        </div>

        {/* COLOR */}
        <div className="control-section">
          <h3 className="control-section-title">3. Color</h3>
          <div className="visual-selector">
            {colorsList.map((c) => (
              <div key={c.id} className={`visual-option-card ${color === c.id ? 'selected' : ''}`} onClick={() => setColor(c.id)}>
                <div className="color-swatch" style={{ backgroundColor: c.hex }}></div>
                <span className="visual-option-label">{c.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* WINDOWS */}
        <div className="control-section">
          <h3 className="control-section-title">4. Windows</h3>
          <div className="visual-selector">
            
            {/* Common: None */}
            <div className={`visual-option-card ${windows === 'none' ? 'selected' : ''}`} onClick={() => setWindows('none')}>
              <div style={{ height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </div>
              <span className="visual-option-label">No Windows</span>
            </div>

            {/* Short Panel Options */}
            {panel !== 'long' && panel !== 'flush' && (
              <>
                <div className={`visual-option-card ${windows === '4pane' ? 'selected' : ''}`} onClick={() => setWindows('4pane')}>
                  <img src={window4paneWhite} alt="Stockton" style={{ filter: 'invert(1)' }} />
                  <span className="visual-option-label">Stockton</span>
                </div>
                <div className={`visual-option-card ${windows === 'prairie' ? 'selected' : ''}`} onClick={() => setWindows('prairie')}>
                  <img src={windowPrairieWhite} alt="Prairie" style={{ filter: 'invert(1)' }} />
                  <span className="visual-option-label">Prairie</span>
                </div>
                <div className={`visual-option-card ${windows === 'diamond' ? 'selected' : ''}`} onClick={() => setWindows('diamond')}>
                  <img src={windowDiamondWhite} alt="Waterford" style={{ filter: 'invert(1)' }} />
                  <span className="visual-option-label">Waterford</span>
                </div>
                <div className={`visual-option-card ${windows === 'sunrise' ? 'selected' : ''}`} onClick={() => setWindows('sunrise')}>
                  <img src={windowSunriseWhite} alt="Wagon Wheel" style={{ filter: 'invert(1)' }} />
                  <span className="visual-option-label">Wagon Wheel</span>
                </div>
              </>
            )}

            {/* Long Panel Options */}
            {panel === 'long' && (
              <div className={`visual-option-card ${windows === '12pane' ? 'selected' : ''}`} onClick={() => setWindows('12pane')}>
                <img src={window12paneWhite} alt="Stockton" style={{ filter: 'invert(1)' }} />
                <span className="visual-option-label">Stockton</span>
              </div>
            )}

            {/* Flush Panel Options */}
            {panel === 'flush' && (
              <>
                <div className={`visual-option-card ${windows === 'flushLeft' ? 'selected' : ''}`} onClick={() => setWindows('flushLeft')}>
                  <img src={flushwindowWhite} alt="Left Windows" style={{ filter: 'invert(1)' }} />
                  <span className="visual-option-label">Left Windows</span>
                </div>
                <div className={`visual-option-card ${windows === 'flushRight' ? 'selected' : ''}`} onClick={() => setWindows('flushRight')}>
                  <img src={flushwindowWhite} alt="Right Windows" style={{ filter: 'invert(1)' }} />
                  <span className="visual-option-label">Right Windows</span>
                </div>
              </>
            )}

          </div>
        </div>

        {/* CTA BUTTON */}
        <div style={{ marginTop: 'auto', paddingTop: '20px' }}>
          <a 
            href={`contact.html?configured=true&configData=${encodeURIComponent(JSON.stringify({
              style: `${size} ${panel === 'beadBoard' ? 'BeadBoard' : panel.charAt(0).toUpperCase() + panel.slice(1)} Panel`,
              color: color.charAt(0).toUpperCase() + color.slice(1),
              windows: windows === 'none' ? 'No Windows' :
                       windows === '4pane' ? 'Stockton' :
                       windows === 'prairie' ? 'Prairie' :
                       windows === 'diamond' ? 'Waterford' :
                       windows === 'sunrise' ? 'Wagon Wheel' :
                       windows === '12pane' ? 'Stockton' :
                       windows === 'flushLeft' ? 'Left Windows' :
                       windows === 'flushRight' ? 'Right Windows' : windows
            }))}`}
            style={{
              display: 'block',
              textAlign: 'center',
              fontFamily: "var(--font-body, 'Inter', sans-serif)",
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: 'var(--bg-primary, #0A0A0A)',
              backgroundColor: 'var(--gold, #C9A84C)',
              padding: '16px 24px',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(201, 168, 76, 0.3)'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'var(--gold-light, #E0C872)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'var(--gold, #C9A84C)'}
          >
            Get your custom door
          </a>
        </div>

      </div>
    </div>
  );
};

export default Configurator;
